import { useEffect, useRef, useState } from "react";
import { getTabId } from "../constants/TabId";
import { LOCAL_STORAGE_KEYS } from "newui/constants/localStorageKeys";

interface LeaderRecord {
	tabId: string;
	ts: number;
}

interface UseSessionHeartbeatOptions {
	activityThrottleMs?: number;
	eventTarget?: EventTarget;
	expiryTime: number | null;
	idleTimeoutMs?: number;
	leaderRenewMs?: number;
	leaderStaleMs?: number;
	refreshIntervalMs?: number;
	warningThresholdMs?: number;
	extendSession: () => Promise<void> | void;
	isExcludedRoute: () => boolean;
	now?: () => number;
}

interface UseSessionHeartbeatReturn {
	isIdle: boolean;
	isLeader: boolean;
}

const LEADER_KEY = LOCAL_STORAGE_KEYS.SESSION_LEADER;
const ACTIVITY_KEY = LOCAL_STORAGE_KEYS.SESSION_LAST_ACTIVITY;

function readLeader(): LeaderRecord | null {
	try {
		const raw = localStorage.getItem(LEADER_KEY);
		if (!raw) {
			return null;
		}
		return JSON.parse(raw) as LeaderRecord;
	} catch {
		return null;
	}
}

function writeLeader(tabId: string, ts: number): void {
	localStorage.setItem(LEADER_KEY, JSON.stringify({ tabId, ts }));
}

function clearLeader(): void {
	localStorage.removeItem(LEADER_KEY);
}

function readLastActivity(): number {
	const raw = localStorage.getItem(ACTIVITY_KEY);
	return raw ? Number(raw) : 0;
}

function writeLastActivity(ts: number): void {
	localStorage.setItem(ACTIVITY_KEY, String(ts));
}

interface LatestOptions {
	activityThrottleMs: number;
	idleTimeoutMs: number;
	leaderStaleMs: number;
	warningThresholdMs: number;
	extendSession: () => Promise<void> | void;
	isExcludedRoute: () => boolean;
	now: () => number;
}

const useSessionHeartbeat = ({
	expiryTime,
	extendSession,
	isExcludedRoute,
	warningThresholdMs = 2 * 60 * 1000,
	refreshIntervalMs = 2 * 60 * 1000,
	activityThrottleMs = 60 * 1000,
	idleTimeoutMs = 3 * 60 * 1000,
	leaderRenewMs = 2500,
	leaderStaleMs = 5000,
	now = Date.now,
	eventTarget,
}: UseSessionHeartbeatOptions): UseSessionHeartbeatReturn => {
	const [isIdle, setIsIdle] = useState(false);
	const [isLeader, setIsLeader] = useState(false);
	const lastRefreshRef = useRef<number>(0);
	const tabId = useRef(getTabId()).current;

	// Store latest callback/value deps in a ref so the effect always reads
	// current values without needing them in the dependency array.
	const latestRef = useRef<LatestOptions>({
		activityThrottleMs,
		extendSession,
		idleTimeoutMs,
		isExcludedRoute,
		leaderStaleMs,
		now,
		warningThresholdMs,
	});
	latestRef.current = {
		activityThrottleMs,
		extendSession,
		idleTimeoutMs,
		isExcludedRoute,
		leaderStaleMs,
		now,
		warningThresholdMs,
	};

	useEffect(() => {
		const target = eventTarget || document;

		const getLatest = () => latestRef.current;

		const isExpired = (): boolean => {
			return expiryTime !== null && expiryTime <= getLatest().now();
		};

		const isInWarningWindow = (): boolean => {
			if (expiryTime === null) {
				return false;
			}
			const timeLeft = expiryTime - getLatest().now();
			return timeLeft > 0 && timeLeft <= getLatest().warningThresholdMs;
		};

		const tryClaimLeader = (): boolean => {
			const current = readLeader();
			const currentTime = getLatest().now();

			if (
				!current ||
				current.tabId === tabId ||
				currentTime - current.ts > getLatest().leaderStaleMs
			) {
				writeLeader(tabId, currentTime);
				setIsLeader(true);
				return true;
			}

			setIsLeader(false);
			return false;
		};

		const tryRefresh = () => {
			const opts = getLatest();
			if (opts.isExcludedRoute()) {
				return;
			}
			if (isExpired()) {
				return;
			}
			if (isInWarningWindow()) {
				return;
			}

			const currentTime = opts.now();
			const lastActivity = readLastActivity();
			if (currentTime - lastActivity > opts.idleTimeoutMs) {
				setIsIdle(true);
				return;
			}

			const leader = readLeader();
			if (!leader || leader.tabId !== tabId) {
				return;
			}

			try {
				lastRefreshRef.current = opts.now();
				opts.extendSession();
			} catch {
				lastRefreshRef.current = 0;
			}
		};

		// --- Activity tracking ---
		const handleActivity = () => {
			const opts = getLatest();
			const currentTime = opts.now();
			const sinceLastRefresh = currentTime - lastRefreshRef.current;
			writeLastActivity(currentTime);
			setIsIdle(false);

			// If leader and enough time since last refresh, fire immediately on activity
			if (sinceLastRefresh >= opts.activityThrottleMs) {
				const leader = readLeader();
				if (
					leader &&
					leader.tabId === tabId &&
					!opts.isExcludedRoute() &&
					!isExpired() &&
					!isInWarningWindow()
				) {
					try {
						lastRefreshRef.current = currentTime;
						opts.extendSession();
					} catch {
						lastRefreshRef.current = 0;
					}
				}
			}
		};

		const activityEvents = ["mousemove", "keydown", "mousedown", "touchstart"];
		activityEvents.forEach((evt) => {
			target.addEventListener(evt, handleActivity);
		});

		// --- Leader election ---
		tryClaimLeader();

		// Leader renew interval
		const leaderInterval = setInterval(() => {
			const leader = readLeader();
			if (leader && leader.tabId === tabId) {
				writeLeader(tabId, getLatest().now());
				setIsLeader(true);
			} else {
				tryClaimLeader();
			}
		}, leaderRenewMs);

		// --- Refresh interval ---
		const refreshInterval = setInterval(() => {
			tryRefresh();
		}, refreshIntervalMs);

		// --- Visibility / focus handlers ---
		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				const opts = getLatest();
				const refreshedByActivity = lastRefreshRef.current;
				handleActivity();
				const activityAlreadyRefreshed =
					lastRefreshRef.current !== refreshedByActivity;

				const claimed = tryClaimLeader();

				// Only fire resume refresh if handleActivity didn't already refresh
				if (
					!activityAlreadyRefreshed &&
					claimed &&
					!opts.isExcludedRoute() &&
					!isExpired() &&
					!isInWarningWindow()
				) {
					const currentTime = opts.now();
					if (currentTime - readLastActivity() <= opts.idleTimeoutMs) {
						try {
							lastRefreshRef.current = currentTime;
							opts.extendSession();
						} catch {
							lastRefreshRef.current = 0;
						}
					}
				}
			}
		};

		const handleFocus = () => {
			tryClaimLeader();
		};

		window.addEventListener("visibilitychange", handleVisibilityChange);
		window.addEventListener("focus", handleFocus);

		// --- Beforeunload: relinquish leadership ---
		const handleBeforeUnload = () => {
			const leader = readLeader();
			if (leader && leader.tabId === tabId) {
				clearLeader();
			}
		};

		window.addEventListener("beforeunload", handleBeforeUnload);

		// --- Cleanup ---
		return () => {
			activityEvents.forEach((evt) => {
				target.removeEventListener(evt, handleActivity);
			});
			clearInterval(leaderInterval);
			clearInterval(refreshInterval);
			window.removeEventListener("visibilitychange", handleVisibilityChange);
			window.removeEventListener("focus", handleFocus);
			window.removeEventListener("beforeunload", handleBeforeUnload);

			// Relinquish leadership on unmount
			const leader = readLeader();
			if (leader && leader.tabId === tabId) {
				clearLeader();
			}
		};
	}, [expiryTime, eventTarget, leaderRenewMs, refreshIntervalMs, tabId]);

	return { isIdle, isLeader };
};

export { useSessionHeartbeat };
export type { UseSessionHeartbeatOptions, UseSessionHeartbeatReturn };
